import { GlobalOutlined, RightOutlined, UserOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Avatar, Divider, Tooltip, Tag as ATag } from 'antd';
import { HTMLAttributes, useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import { useIsStoreAliasFirst } from 'hooks/business/store/useStoreName';
import { useIsSbsMode } from 'hooks/business/useIsSbs';
import { useMemoBuyerNickMask } from 'services/channels/demo';
import SellerCenter from 'components/SellerCenter';
import { platformLabelMap } from 'services/config/platform';
import { regionBuyerDetailHostMap } from 'services/config/region';
import toLowerCase from 'utils/toLowerCase';
import forwardSvg from 'assets/icons/forward.svg';
import { parse } from 'libphonenumber-js';
import { Between } from './TradeRefund';
import copyWhiteSvg from './assets/copyWhite.svg';
import Copy, { Image } from './Copy';
import { openExternalURL } from 'utils/openExternalURL';
import { getImageThumb } from 'lib/utils/platformImage';
import { useXiapibuyEnable } from 'hooks/useXiapibuyEnable';
import { useGetRegionLabel } from 'hooks/business/useRegion';
import { getPlatformMallURL } from 'utils/mallURL';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { getSellerCenterURL, isSupportSellerCenterURL } from 'utils/sellerCenterURL';

const platformColorMap = {
  shopee: '#ED6735',
  lazada: '#0A32CB',
  wa_biz: '#24D366',
  tokopedia: '#24D366',
  tiktok: '#141514',
  tiktok_us: '#141514',
  aliexpress: '#0A32CB',
  mercado: '#4E5969',
  kwai: '#FF6734',
  facebook: '#225B9E',
};

const platformBorderColorMap = {
  shopee: '#FFDED1',
  lazada: '#D8E0FF',
  wa_biz: '#24D366',
  tokopedia: '#24D366',
  tiktok: '#d0d0d0',
  tiktok_us: '#d0d0d0',
  aliexpress: '#D8E0FF',
  mercado: '#B3C0D6',
  kwai: '#FFDED1',
  facebook: '#225B9E',
};

export const PlatformTag = (
  props: HTMLAttributes<HTMLSpanElement> & { platform: General.PlatformUpperCaseTunnel },
) => {
  const { platform, ...otherProps } = props;

  const lowerCasePlatform = toLowerCase(platform);

  const { [lowerCasePlatform]: label } = platformLabelMap;

  const { [lowerCasePlatform]: color } = platformColorMap;

  const { [lowerCasePlatform]: borderColor } = platformBorderColorMap;

  return (
    <Tag color={color} borderColor={borderColor} {...otherProps}>
      {label}
    </Tag>
  );
};

export interface HeadProps extends HTMLAttributes<HTMLHeadElement> {
  conversation: Conversation.Conversation;
  facebookBuyer?: Conversation.FacebookBuyer;
}

function Head(props: HeadProps) {
  const { conversation, facebookBuyer, ...otherProps } = props;

  const {
    buyerAvatar,
    buyerNick: buyerNickRaw,
    channel,
    region,
    topBarInfo,
    storeId,
  } = conversation;
  const buyerNick = useMemoBuyerNickMask(buyerNickRaw);

  if (!topBarInfo) {
    return <div className="min-h-[76px] mb-4 bg-fill-0" />;
  }

  const {
    storeName,
    storeAlias,
    storeType,
    tunnelName,
    negRate,
    cancelOrder,
    isSellerAffiliateChat,
  } = topBarInfo;

  return (
    <HeadComponent
      storeId={storeId}
      info={{
        buyerAvatar,
        buyerNick,
        channel,
        region,
        storeName,
        storeType,
        storeAlias,
        tunnelName,
        negRate,
        cancelOrder,
        isSellerAffiliateChat,
      }}
      conversation={conversation}
      facebookBuyer={facebookBuyer}
      {...otherProps}
    />
  );
}

export interface HeadComponentProps extends HTMLAttributes<HTMLHeadElement> {
  storeId: string;
  info: {
    buyerAvatar?: string;
    buyerNick: string;
    channel: General.PlatformUpperCaseTunnel;
    region: General.UpperCaseRegion;
    storeName: string;
    storeType?: Conversation.Trade.IStoreType;
    storeAlias: string;
    tunnelName?: string;
    negRate: boolean;
    cancelOrder: boolean;
    isSellerAffiliateChat: boolean;
  };
  conversation?: Conversation.Conversation;
  facebookBuyer?: Conversation.FacebookBuyer;
}

export function HeadComponent(props: HeadComponentProps) {
  const {
    storeId,
    conversation,
    info: {
      buyerAvatar,
      buyerNick: buyerNickRaw,
      channel,
      region,
      storeName,
      storeAlias,
      storeType,
      tunnelName,
      negRate,
      cancelOrder,
      isSellerAffiliateChat,
    },
    facebookBuyer,
    ...otherProps
  } = props;
  const buyerNick = useMemoBuyerNickMask(buyerNickRaw);
  const isSbsMode = useIsSbsMode();
  const { whiteListConfig: { WA_MOBILE_SHOW = false } = {} } = useOrgConfigStateValue();

  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();

  const isStoreAliasFirst = useIsStoreAliasFirst(channel, region);

  const xiapibuyEnable = useXiapibuyEnable(region);

  const storeTypeUI = storeType ? t(`店铺类型枚举.${storeType}`) : '';
  const countryCode = parse(`+${conversation?.externalBuyerId?.replace('+', '')}`).country;
  // 这里只是为了绕过类型检查，countryCode 的返回比Region要大的多
  const phoneRegion: General.Region = toLowerCase(countryCode) as any;

  const mallURL = useMemo(() => getPlatformMallURL(channel, region), [channel, region]);

  const showMobile = useMemo(
    () => conversation?.channel === 'WA_BIZ' && WA_MOBILE_SHOW,
    [WA_MOBILE_SHOW, conversation],
  );

  return (
    <Container {...otherProps}>
      <Avatar
        shape="circle"
        size={40}
        src={buyerAvatar ? getImageThumb(buyerAvatar, channel) : undefined}
        icon={buyerAvatar ? undefined : <UserOutlined />}
      />
      <div className="flex flex-1 flex-col overflow-hidden gap-1">
        <div className="flex gap-4 items-center justify-between">
          <div className="flex items-center flex-1 overflow-hidden flex-wrap gap-x-2 gap-y-1">
            <div className="overflow-hidden whitespace-nowrap text-ellipsis">{buyerNick}</div>
            {showMobile && conversation?.externalBuyerId && (
              <div className="text-content-300">{conversation?.externalBuyerId}</div>
            )}
            {conversation?.topBarInfo?.maliciousBuyer ? (
              <div className="text-xs py-0.5 px-1 bg-fill-error text-error rounded-sm">
                {t('恶意买家')}
              </div>
            ) : (
              (conversation?.topBarInfo?.buyerNegativeTag ||
                conversation?.topBarInfo?.buyerStoreNegativeTag) && (
                <Tooltip title={t('chat_side_negative_buyer_tag_tips')}>
                  <div className="text-xs py-0.5 px-1 bg-fill-error text-error rounded-sm">
                    {t('chat_side_negative_buyer_tag')}
                  </div>
                </Tooltip>
              )
            )}
            {negRate && (
              <Tooltip title={t('sys_neg_rate_message_content')}>
                <BuyerTag>{t('review_need_reception')}</BuyerTag>
              </Tooltip>
            )}
            {cancelOrder && (
              <Tooltip title={t('sys_cancel_order_message_content')}>
                <BuyerTag>{t('order_need_reception')}</BuyerTag>
              </Tooltip>
            )}
            {isSellerAffiliateChat && (
              <div className="bg-[#ebfaf3] rounded-[2px] leading-[18px] px-[4px] text-[#00BF69] text-[12px]">
                {t('达人')}
              </div>
            )}
          </div>
          {channel === 'SHOPEE' && !isSbsMode && (
            <div
              className="text-xs text-content-200 cursor-pointer"
              onClick={() => {
                let host = `https://${toLowerCase(region)}.xiapibuy.com`;
                if (!xiapibuyEnable) {
                  const r = regionBuyerDetailHostMap[toLowerCase(region)];
                  if (r) {
                    host = r;
                  }
                }

                const path = `${host}/${buyerNick}`;
                openExternalURL(path, '__blank');
              }}
            >
              {t('review.更多')}
              <RightOutlined />
            </div>
          )}
        </div>
        <Detail>
          <PlatformTag platform={channel} />
          {channel === 'FACEBOOK' && facebookBuyer?.locale && (
            <div>
              <GlobalOutlined style={{ margin: '0 6px ' }} />
              {t(`langs.${facebookBuyer.locale}`, facebookBuyer.locale)}
            </div>
          )}
          {channel !== 'FACEBOOK' && [
            <Wrapper key="region-wrapper">
              <div>
                {channel === 'WA_BIZ'
                  ? t({
                      key: `region.${toLowerCase(phoneRegion)}`,
                      defaultValue: phoneRegion ?? '-',
                    })
                  : getRegionLabel(region)}
              </div>
              {mallURL ? (
                <Tooltip title={t('scene.chatWorkspace.orderTab.tooltip.mallHomepage')}>
                  <LinkButton onClick={() => openExternalURL(mallURL, '__blank')}>
                    <img src={forwardSvg} alt="forward" />
                  </LinkButton>
                </Tooltip>
              ) : null}
            </Wrapper>,
            <Divider key="region-divider" type="vertical" />,
          ]}
          {channel === 'FACEBOOK' ? null : channel === 'WA_BIZ' ? (
            <Wrapper>
              <div>{tunnelName}</div>
            </Wrapper>
          ) : (
            <StoreWrapper>
              <Store>
                <Tooltip
                  title={
                    <StoreNamePop>
                      {storeAlias ? (
                        <div>
                          <Between>
                            <div>{t('ViewShops.店铺别名')}</div>
                            <Copy
                              text={storeAlias}
                              successMessage={t('common.feedback.copySuccess')}
                              failedMessage={t('common.feedback.copyFailed')}
                            >
                              <Image src={copyWhiteSvg} alt="copy" />
                            </Copy>
                          </Between>
                          <div>{storeAlias}</div>
                        </div>
                      ) : null}
                      <div>
                        <Between>
                          <div>{t('ViewShops.店铺名称')}</div>
                          <Copy
                            text={storeName}
                            successMessage={t('common.feedback.copySuccess')}
                            failedMessage={t('common.feedback.copyFailed')}
                          >
                            <Image src={copyWhiteSvg} alt="copy" />
                          </Copy>
                        </Between>
                        <div>{storeName}</div>
                      </div>
                    </StoreNamePop>
                  }
                >
                  <StoreNameWrapper>
                    <LinkButton>
                      <StoreName>
                        {isStoreAliasFirst ? storeAlias || storeName : storeName}
                      </StoreName>
                    </LinkButton>
                  </StoreNameWrapper>
                </Tooltip>
                {storeTypeUI ? <TagStore>{storeTypeUI}</TagStore> : null}
                {isSupportSellerCenterURL(channel) ? (
                  <Tooltip title={t('scene.chatWorkspace.orderTab.action.viewSellerCenter')}>
                    <div>
                      <SellerCenter platform={channel}>
                        <LinkButton
                          onClick={() => {
                            getSellerCenterURL(channel, storeId);
                          }}
                        >
                          <img src={forwardSvg} alt="forward" />
                        </LinkButton>
                      </SellerCenter>
                    </div>
                  </Tooltip>
                ) : null}
              </Store>
            </StoreWrapper>
          )}
        </Detail>
      </div>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  background: #fff;
  overflow: hidden;
  margin-bottom: 16px;
  > span {
    margin-right: 8px;
  }
`;

const Detail = styled.div`
  display: flex;
  align-items: center;
  color: #515359;
  overflow: hidden;
  > * {
    flex: 0 0 auto;
    margin-right: 8px;
    margin-left: 0;
  }
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  height: 20px;
  padding: 0 2px;
  font-size: 13px;
  > img {
    width: 14px;
    height: 14px;
    margin: 0 4px;
  }
`;

export const LinkButton = styled(Wrapper)`
  border-radius: 2px;
  color: #515359;
  cursor: pointer;
  :hover {
    background: #f0f2f5;
  }
`;

const StoreWrapper = styled.div`
  flex: 1 0 0;
  display: flex;
  overflow: hidden;
  margin-right: 0;
  > div {
    max-width: 100%;
  }
`;

const Store = styled(Wrapper)`
  overflow: hidden;
`;

const StoreNameWrapper = styled.div`
  overflow: hidden;
  margin-right: 0;
  > div {
    max-width: 100%;
  }
`;

const StoreName = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StoreNamePop = styled.div`
  font-size: 12px;
  color: #fff;
  > div {
    margin-bottom: 8px;
  }
`;

const Tag = styled.span<{ color: string; borderColor: string }>`
  display: inline-block;
  height: 20px;
  padding: 0 4px;
  line-height: 20px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ color }) => color};
  border: ${({ borderColor }) => `solid 1px ${borderColor}`};
  border-radius: 2px;
`;

const TagStore = styled(ATag)`
  height: 20px;
  padding: 0 4px;
  line-height: 18px;
  margin-right: 0;
`;

const BuyerTag = styled.div`
  padding: 2px 4px;
  line-height: 16px;
  font-size: 12px;
  color: #ff9f18;
  background: #fffaf0;
  border-radius: 2px;
  cursor: pointer;
`;

export default Head;
